import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "popupHost"]
  static values = { popupId: String }

  connect() {
    if (this.hasTriggerTarget) {
      if (this.hasPopupIdValue) {
        this.triggerTarget.setAttribute("aria-controls", this.popupIdValue)
      }
      this.triggerTarget.setAttribute("aria-haspopup", "true")
    }
    // Defensive reset: if popup is closed on connect, ensure page scroll is not locked.
    if (!this.isPopupOpen()) {
      document.body.style.overflow = ""
    }
    this.observePopupVisibility()
    this.syncAriaExpanded()
  }

  disconnect() {
    this.disconnectPopupObserver()
  }

  toggle(event) {
    event.preventDefault()
    if (this.isPopupOpen()) {
      this.close()
      return
    }

    this.open()
  }

  open() {
    const popupElement = this.popupElement()
    if (!popupElement) {
      return
    }

    popupElement.classList.remove("hidden")
    this.initializePopupController(popupElement)
    this.syncAriaExpanded()
  }

  close(_event) {
    const popupElement = this.popupElement()
    if (!popupElement) {
      return
    }

    const popupController = this.popupController(popupElement)
    if (popupController) {
      popupController.close()
    } else {
      popupElement.classList.add("hidden")
    }

    this.syncAriaExpanded()
  }

  popupElement() {
    if (!this.hasPopupHostTarget) {
      return null
    }

    return this.popupHostTarget.querySelector('[data-controller~="popup"], [data-controller*="popup"]')
  }

  popupController(popupElement = this.popupElement()) {
    if (!popupElement) {
      return null
    }

    return this.application.getControllerForElementAndIdentifier(popupElement, "popup")
  }

  isPopupOpen() {
    const popupElement = this.popupElement()
    return Boolean(popupElement) && !popupElement.classList.contains("hidden")
  }

  initializePopupController(popupElement) {
    const popupController = this.popupController(popupElement)
    if (popupController) {
      popupController.initialize()
      return
    }

    requestAnimationFrame(() => {
      const delayedPopupController = this.popupController(popupElement)
      if (delayedPopupController) {
        delayedPopupController.initialize()
      }
    })
  }

  observePopupVisibility() {
    const popupElement = this.popupElement()
    if (!popupElement) {
      return
    }

    this.popupObserver = new MutationObserver(() => {
      this.syncAriaExpanded()
    })
    this.popupObserver.observe(popupElement, { attributes: true, attributeFilter: ["class"] })
  }

  disconnectPopupObserver() {
    if (!this.popupObserver) {
      return
    }

    this.popupObserver.disconnect()
    this.popupObserver = null
  }

  syncAriaExpanded() {
    if (!this.hasTriggerTarget) {
      return
    }

    this.triggerTarget.setAttribute("aria-expanded", String(this.isPopupOpen()))
  }
}

